import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  User,
  Settings,
  Lock,
  X,
  Loader2,
  Eye,
  EyeOff,
  CheckCircle2,
  Check,
  Circle,
} from "lucide-react";
import Badge from "../components/Badge";
import LoadingSkeleton from "../components/LoadingSkeleton";
import { useAuth } from "../context/AuthContext";
import { useQuery } from "../lib/useQuery";
import { getStudent, markPasswordSet } from "../lib/queries";

const PASSWORD_RULES = [
  { id: "length", label: "Mínimo 8 caracteres", test: (pw) => pw.length >= 8 },
  { id: "upper", label: "Una mayúscula", test: (pw) => /[A-Z]/.test(pw) },
  { id: "lower", label: "Una minúscula", test: (pw) => /[a-z]/.test(pw) },
  {
    id: "special",
    label: "Un número o símbolo",
    test: (pw) => /[0-9!@#$%^&*(),.?":{}|<>_\-+=[\]\\;'/`~]/.test(pw),
  },
];

function PasswordDrawer({ userId, isOpen, onClose }) {
  const { updatePassword } = useAuth();
  const [visible, setVisible] = useState(false);
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      requestAnimationFrame(() => setVisible(true));
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const handleClose = () => {
    setVisible(false);
    setTimeout(() => {
      setPw("");
      setConfirm("");
      setError(null);
      setSuccess(false);
      onClose();
    }, 200);
  };

  const passedRules = PASSWORD_RULES.filter((r) => r.test(pw));
  const allRulesPassed = passedRules.length === PASSWORD_RULES.length;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!allRulesPassed) {
      setError("La contraseña no cumple todos los requisitos");
      return;
    }
    if (pw !== confirm) {
      setError("Las contraseñas no coinciden");
      return;
    }

    setLoading(true);
    try {
      await updatePassword(pw);
      await markPasswordSet(userId);
      setSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const content = (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-white">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
          <Lock size={18} className="text-brand" />
          {success ? "Contraseña actualizada" : "Cambiar Contraseña"}
        </h3>
        <button
          onClick={handleClose}
          className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X size={18} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        {success ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 size={32} className="text-emerald-500" />
            </div>
            <h4 className="text-lg font-semibold text-gray-900 mb-2">
              ¡Contraseña actualizada!
            </h4>
            <p className="text-sm text-gray-600 mb-6">
              Tu contraseña se ha actualizado correctamente. Ya puedes usarla
              para iniciar sesión.
            </p>
            <button
              onClick={handleClose}
              className="px-6 py-3 bg-brand text-white rounded-xl text-sm font-semibold hover:bg-brand-dark transition-all"
            >
              Cerrar
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Nueva contraseña
              </label>
              <div className="relative">
                <input
                  type={show ? "text" : "password"}
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  placeholder="Introduce tu nueva contraseña"
                  className="w-full px-4 py-3 pr-10 border-[1.5px] border-gray-200 rounded-xl text-sm bg-gray-50 outline-none transition-all focus:border-brand focus:bg-white focus:ring-3 focus:ring-brand/10"
                />
                <button
                  type="button"
                  onClick={() => setShow(!show)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {show ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>

              {pw.length > 0 && (
                <div className="mt-3 p-3 bg-gray-50 rounded-xl space-y-2">
                  {PASSWORD_RULES.map((rule) => {
                    const passed = rule.test(pw);
                    return (
                      <div
                        key={rule.id}
                        className={`flex items-center gap-2 text-sm transition-colors ${
                          passed ? "text-emerald-600" : "text-gray-400"
                        }`}
                      >
                        {passed ? (
                          <Check size={14} className="shrink-0" />
                        ) : (
                          <Circle size={14} className="shrink-0" />
                        )}
                        {rule.label}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Confirmar contraseña
              </label>
              <input
                type={show ? "text" : "password"}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="Repite la contraseña"
                className="w-full px-4 py-3 border-[1.5px] border-gray-200 rounded-xl text-sm bg-gray-50 outline-none transition-all focus:border-brand focus:bg-white focus:ring-3 focus:ring-brand/10"
              />
              {confirm.length > 0 && pw !== confirm && (
                <p className="mt-2 text-sm text-amber-600">
                  Las contraseñas no coinciden
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading || !allRulesPassed || pw !== confirm}
              className="w-full py-3 bg-brand hover:bg-brand-dark text-white rounded-xl text-sm font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Guardando...
                </>
              ) : (
                "Guardar contraseña"
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );

  const drawer = (
    <div className="fixed inset-0 z-[99999]">
      <div
        className={`fixed inset-0 bg-black/40 transition-opacity duration-200 ${
          visible ? "opacity-100" : "opacity-0"
        }`}
        onClick={handleClose}
      />

      {/* Desktop: drawer from right */}
      <div
        className={`
          hidden md:flex fixed top-0 right-0 h-full w-full max-w-md
          flex-col bg-white shadow-2xl
          transition-transform duration-200 ease-out
          ${visible ? "translate-x-0" : "translate-x-full"}
        `}
      >
        {content}
      </div>

      {/* Mobile: drawer from bottom */}
      <div
        className={`
          md:hidden fixed inset-0 bg-white
          flex flex-col
          transition-transform duration-200 ease-out
          ${visible ? "translate-y-0" : "translate-y-full"}
        `}
      >
        {content}
      </div>
    </div>
  );

  return createPortal(drawer, document.body);
}

export default function Profile() {
  const { user } = useAuth();
  const { data: student, loading } = useQuery(
    () => getStudent(user.id),
    [user.id]
  );
  const [showPwModal, setShowPwModal] = useState(false);

  if (loading) return <LoadingSkeleton />;
  if (!student) return null;

  const fields = [
    { label: "Email", value: student.email },
    { label: "Teléfono", value: student.phone },
    { label: "Fecha de Inicio", value: student.start_date },
  ];

  const initials = student.name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="animate-fade-in grid grid-cols-1 lg:grid-cols-2 gap-6">
      <PasswordDrawer
        userId={user.id}
        isOpen={showPwModal}
        onClose={() => setShowPwModal(false)}
      />

      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        <div className="px-4 md:px-6 py-4 border-b border-gray-100">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <User size={18} className="text-brand" />
            Información Personal
          </h3>
        </div>
        <div className="p-4 md:p-6">
          <div className="flex items-center gap-4 mb-6 pb-6 border-b border-gray-100">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-brand to-brand-accent flex items-center justify-center text-white font-bold text-xl">
              {initials}
            </div>
            <div>
              <h3 className="text-lg font-semibold">{student.name}</h3>
              <p className="text-sm text-gray-500">{student.program}</p>
              <Badge variant="info">{student.stage}</Badge>
            </div>
          </div>

          <div className="space-y-0">
            {fields.map(({ label, value }) => (
              <div
                key={label}
                className="flex justify-between py-3 border-b border-gray-100 last:border-0"
              >
                <span className="text-sm text-gray-500">{label}</span>
                <span className="text-sm font-medium text-gray-900">
                  {value}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="space-y-6">
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <div className="px-4 md:px-6 py-4 border-b border-gray-100">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <Settings size={18} className="text-gray-500" />
              Configuración
            </h3>
          </div>
          <div className="p-4 md:p-6">
            <button
              onClick={() => setShowPwModal(true)}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg border border-gray-200 text-sm text-left text-gray-700 transition-all hover:border-gray-300 hover:bg-gray-50"
            >
              <Lock size={16} />
              Cambiar Contraseña
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
